import { Printer } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button, Money } from "@/components/ui";
import type { SalePublic } from "@/lib/endpoints";
import { printTicket } from "@/lib/printing";
import { getSession } from "@/lib/session";
import { getEffectiveTenantSettings } from "@/lib/theme";

/**
 * Confirmación de la venta.
 *
 * Muestra el folio **grande** porque es lo que el cajero necesita para
 * verificarlo contra el ticket impreso, y ofrece imprimir y empezar otra venta
 * sin soltar el teclado: `Enter` cierra y `F7` imprime.
 */
export function ReceiptDialog({
  folio,
  sale,
  onClose,
}: {
  folio: string;
  sale: SalePublic | null;
  onClose: () => void;
}) {
  const [printing, setPrinting] = useState(false);
  const [printed, setPrinted] = useState<string | null>(null);
  const autoPrintedRef = useRef(false);

  async function handlePrint() {
    if (!sale) return;
    setPrinting(true);
    const session = getSession();
    const settings = getEffectiveTenantSettings(session?.tenant);
    const destination = await printTicket(sale, {
      businessName: session?.tenant.name ?? "MegaSyR",
      branchName: session?.branches[0]?.name ?? "",
      cashierName: session?.user.full_name ?? "",
      paperWidth: settings.ticket_paper_width === "58mm" ? 58 : 80,
      slogan: settings.ticket_show_slogan ? settings.commercial_slogan : undefined,
      rfc: settings.ticket_show_rfc ? session?.tenant.rfc : undefined,
      taxRegime: settings.ticket_show_regime ? session?.tenant.tax_regime : undefined,
      address: settings.ticket_show_address ? (settings.contact_address || session?.branches[0]?.address) : undefined,
      phone: settings.ticket_show_phone ? (settings.contact_phone || session?.branches[0]?.phone) : undefined,
      showCashier: settings.ticket_show_cashier,
      showBranch: settings.ticket_show_branch,
      showFolio: settings.ticket_show_folio,
      showTaxBreakdown: settings.ticket_show_tax_breakdown,
      headerNote: settings.ticket_header_note,
      footerNote: settings.ticket_footer_note,
      billingInstructions: settings.ticket_billing_instructions,
    });
    setPrinted(destination);
    setPrinting(false);
  }

  useEffect(() => {
    const session = getSession();
    const settings = getEffectiveTenantSettings(session?.tenant);
    if (settings.ticket_auto_print && !autoPrintedRef.current && sale) {
      autoPrintedRef.current = true;
      void handlePrint();
    }
  }, [sale]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Enter" || event.key === "Escape" || event.key === "F7") {
        event.preventDefault();
        if (event.key === "F7") void handlePrint();
        else onClose();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div className="w-full max-w-md space-y-5 rounded-msr-lg border border-line bg-snow p-6 text-center text-carbon shadow-2xl">
        <div>
          <p className="text-xs tracking-widest text-muted uppercase">Venta registrada</p>
          <p className="font-display text-4xl font-extrabold text-carbon">{folio}</p>
        </div>

        {sale ? (
          <dl className="space-y-1 rounded-msr border border-line bg-surface/30 p-4 text-sm">
            <Row label="Total">
              <Money cents={sale.total_cents} size="md" className="font-semibold text-carbon" />
            </Row>
            {sale.change_cents > 0 ? (
              <Row label="Cambio">
                <Money cents={sale.change_cents} size="md" className="text-success font-semibold" />
              </Row>
            ) : null}
            {sale.tip_cents > 0 ? (
              <Row label="Propina">
                <Money cents={sale.tip_cents} size="md" className="text-carbon" />
              </Row>
            ) : null}
            <Row label="Pago">
              <span className="capitalize text-carbon font-medium">
                {(sale.payments ?? []).map((payment) => payment.method).join(" + ")}
              </span>
            </Row>
          </dl>
        ) : (
          <p className="rounded-msr border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
            Sin conexión: la venta quedó en la cola y se enviará sola. El folio definitivo se
            asigna al sincronizar.
          </p>
        )}

        {printed ? (
          <p className="text-xs text-muted">
            {printed === "usb"
              ? "Impreso directamente vía USB térmico (ESC/POS)."
              : printed === "bluetooth"
                ? "Impreso directamente vía Bluetooth térmico (ESC/POS)."
                : printed === "agente"
                  ? "Enviado a la impresora del local."
                  : "Abierto el diálogo de impresión del navegador."}
          </p>
        ) : null}

        <div className="flex gap-3">
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            disabled={!sale}
            loading={printing}
            onClick={handlePrint}
            icon={<Printer aria-hidden className="size-4" />}
          >
            Imprimir <span className="text-[10px] opacity-60">F7</span>
          </Button>
          <Button size="lg" fullWidth onClick={onClose}>
            Nueva venta
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className="text-carbon font-medium">{children}</dd>
    </div>
  );
}
